<template>
  <div class="forgot-password-modal">
    <div class="forgot-password-overlay" @click="closeForgotPassword"></div>
    <div class="forgot-password-content">
      <div class="forgot-password-header">
        <h3>{{ mode === 'change' ? 'Change Password' : 'Retrieve Password' }}</h3>
        <button class="close-btn" @click="closeForgotPassword">
          <i class="el-icon-close"></i>
        </button>
      </div>
      
      <div class="forgot-password-body">
        <!-- 选择方式 -->
        <div class="method-selector">
          <div 
            :class="['method-option', { 'active': resetMethod === 'email' }]"
            @click="resetMethod = 'email'"
          >
            <i class="el-icon-message"></i>
            <span>Email</span>
          </div>
          <div 
            :class="['method-option', { 'active': resetMethod === 'phone' }]"
            @click="resetMethod = 'phone'"
          >
            <i class="el-icon-phone"></i>
            <span>Phone</span>
          </div>
        </div>

        <!-- 邮箱输入 -->
        <div class="input-box" v-if="resetMethod === 'email'">
          <div class="shadow-box full-width">
            <input 
              class="input" 
              v-model="form.email" 
              :placeholder="mode === 'change' ? 'Your email address' : 'Email address'"
              :disabled="mode === 'change'"
              type="email"
            />
          </div>
        </div>

        <!-- 手机号输入 -->
        <div class="input-box" v-if="resetMethod === 'phone'">
          <div class="area-box">
            <PhoneBtn v-model="form.code"></PhoneBtn>
          </div>
          <div class="shadow-box">
            <input 
              class="input" 
              v-model="form.phone" 
              @keyup="form.phone=form.phone.replace(/\D/g,'')" 
              placeholder="Phone number" 
              type="tel" 
            />
          </div>
        </div>

        <!-- 新密码输入 -->
        <div class="input-box">
          <div class="shadow-box full-width">
            <input 
              class="input" 
              v-model="form.newPassword" 
              :type="showPassword ? 'text' : 'password'"
              placeholder="New Password"
            />
            <i 
              class="el-icon-view password-toggle"
              :class="{ 'show': showPassword }"
              @click="showPassword = !showPassword"
            ></i>
          </div>
        </div>

        <!-- 验证码输入 -->
        <div class="input-box">
          <div class="shadow-box" style="flex: 1;">
            <input 
              class="input" 
              v-model="form.verificationCode" 
              placeholder="Verification Code"
              type="text"
              @keyup="form.verificationCode=form.verificationCode.substring(0,6)"
            />
          </div>
          <button 
            :class="['send-code-btn', { 'disabled': !canSendCode || sendingCode }]"
            @click="sendVerificationCode"
            :disabled="!canSendCode || sendingCode"
          >
            <i v-if="sendingCode" class="el-icon-loading"></i>
            {{ sendCodeText }}
          </button>
        </div>

        <!-- 提交按钮 -->
        <div 
          :class="['submit-btn', { 'disabled': !canSubmit || submitting }]"
          @click="submitReset"
        >
          <i v-if="submitting" class="el-icon-loading" style="margin-right: 8px;"></i>
          {{ submitting ? 'Updating...' : 'Confirm' }}
        </div>
      </div>
    </div>

    <!-- Cloudflare 验证弹层 -->
    <div v-if="showCloudflare" class="cloudflare-modal">
      <div class="cloudflare-overlay" @click="closeCloudflare"></div>
      <div class="cloudflare-content">
        <div class="cloudflare-header">
          <h3>Security Verification</h3>
          <button class="close-btn" @click="closeCloudflare">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <CloudflareVerify 
          style="padding: 20px;" 
          ref="cloudflare"
          @token-received="onTokenReceived"
        ></CloudflareVerify>
      </div>
    </div>
  </div>
</template>

<script>
import { postGateway } from '@/request';
import PhoneBtn from '@/components/PhoneBtn.vue';
import CloudflareVerify from '@/components/CloudFlareVerify.vue';

export default {
  name: 'ForgotPassword',
  props: {
    mode: {
      type: String,
      default: 'forgot', // 'forgot' 或 'change'
      validator: value => ['forgot', 'change'].includes(value)
    }
  },
  components: {
    PhoneBtn,
    CloudflareVerify
  },
  data() {
    return {
      resetMethod: this.mode === 'change' ? 'email' : 'email', // 修改密码模式默认使用邮箱
      showPassword: false,
      sendingCode: false,
      submitting: false,
      showCloudflare: false,
      countdown: 0,
      countdownTimer: null,
      form: {
        email: '',
        phone: '',
        code: '+44',
        newPassword: '',
        verificationCode: ''
      }
    }
  },
  computed: {
    canSendCode() {
      if (this.countdown > 0) return false;
      if (this.resetMethod === 'email') {
        return this.form.email.trim() !== '';
      } else {
        return this.form.phone.trim() !== '';
      }
    },
    canSubmit() {
      if (this.resetMethod === 'email') {
        return this.form.email.trim() !== '' && 
               this.form.newPassword.trim() !== '' && 
               this.form.verificationCode.trim() !== '';
      } else {
        return this.form.phone.trim() !== '' && 
               this.form.newPassword.trim() !== '' && 
               this.form.verificationCode.trim() !== '';
      }
    },
    sendCodeText() {
      if (this.countdown > 0) {
        return `${this.countdown}s`;
      }
      if (this.sendingCode) {
        return 'Sending...';
      }
      return 'Send Code';
    }
  },
  mounted() {
    // 如果是修改密码模式，预填充用户邮箱
    if (this.mode === 'change') {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        try {
          const user = JSON.parse(userInfo);
          this.form.email = user.emailAddress || user.email || '';
        } catch (error) {
          console.error('Failed to parse user info:', error);
        }
      }
    }
  },
  methods: {
    // 关闭忘记密码弹窗
    closeForgotPassword() {
      this.$emit('close');
    },

    // 关闭 Cloudflare 弹层
    closeCloudflare() {
      this.showCloudflare = false;
      this.resetCode();
    },

    // 重置验证码
    resetCode() {
      if (this.$refs.cloudflare) {
        this.$refs.cloudflare.reSetTurnSite();
      }
    },

    // Cloudflare token接收回调
    onTokenReceived(token) {
      if (token) {
        this.showCloudflare = false;
        this.sendVerificationCodeWithToken(token);
      }
    },

    // 发送验证码
    async sendVerificationCode() {
      if (!this.canSendCode) return;

      if (this.resetMethod === 'email') {
        await this.sendEmailCode();
      } else {
        await this.sendPhoneCode();
      }
    },

    // 发送邮箱验证码
    async sendEmailCode() {
      try {
        this.sendingCode = true;

        // 只有在忘记密码模式下才检查邮箱是否存在
        if (this.mode === 'forgot') {
          const checkResp = await postGateway({
            url: '/customerApp/index/checkEmailNoExist',
            method: 'GET',
            data: { email: this.form.email.trim() }
          });

          if (checkResp.code !== 200) {
            this.$message.error(checkResp.msg || 'Email not found');
            return;
          }
        }

        // 发送验证码
        const captchaType = this.mode === 'change' ? 'changePassword' : 'forgetPassword';
        const resp = await postGateway({
          url: `/customer/atClient/emailCaptcha?email=${this.form.email.trim()}&captchaType=${captchaType}`,
          method: 'POST',
          data: {
            email: this.form.email.trim(),
            captchaType: captchaType
          }
        });

        if (resp.code === 200) {
          this.$message.success('Verification code sent successfully!');
          this.startCountdown();
        }
      } catch (error) {
        if(error.msg) {
          this.$message.error(error.msg);
        }
      } finally {
        this.sendingCode = false;
      }
    },

    // 发送手机验证码
    async sendPhoneCode() {
      try {
        this.sendingCode = true;

        const fullPhone = this.form.code + this.form.phone;
        
        // 检查手机号是否存在
        const checkResp = await postGateway({
          url: '/customerApp/index/checkPhoneNoExist',
          method: 'GET',
          data: { phone: fullPhone }
        });

        if (checkResp.code !== 200) {
          this.$message.error(checkResp.msg || 'Phone number not found');
          return;
        }

        // 显示 Cloudflare 验证
        this.showCloudflare = true;
      } catch (error) {
        if(error.msg) {
          this.$message.error(error.msg);
        }
        this.sendingCode = false;
      }
    },

    // 使用token发送手机验证码
    async sendVerificationCodeWithToken(token) {
      try {
        this.sendingCode = true;

        const fullPhone = this.form.code + this.form.phone;
        
        const resp = await postGateway({
          url: '/customer/atClient/smsSendCloudFlare',
          method: 'POST',
          data: {
            phone: fullPhone,
            captchaType: 'forgetPassword',
            captchaVerification: token
          }
        });

        if (resp.code === 200) {
          this.$message.success('Verification code sent successfully!');
          this.startCountdown();
        } else {
          this.$message.error(resp.msg || 'Failed to send verification code');
        }
      } catch (error) {
        if(error.msg) {
          this.$message.error(error.msg);
        }
      } finally {
        this.sendingCode = false;
      }
    },

    // 开始倒计时
    startCountdown() {
      this.countdown = 60;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
      }, 1000);
    },

    // 提交重置密码
    async submitReset() {
      if (!this.canSubmit || this.submitting) return;

      try {
        this.submitting = true;

        const params = {
          passWord: this.$encryptByPublicKey(this.form.newPassword.trim()),
          captcha: this.form.verificationCode
        };

        if (this.resetMethod === 'email') {
          params.email = this.form.email.trim();
        } else {
          params.phone = this.form.code + this.form.phone;
        }

        const resp = await postGateway({
          url: '/customer/atClient/editPassWord',
          method: 'POST',
          data: params
        });

        if (resp.code === 200) {
          this.$message.success('Password updated successfully!');
          this.closeForgotPassword();
        } else {
          this.$message.error(resp.msg || 'Failed to update password');
        }
      } catch (error) {
        if(error.msg && error.code != 50028) {
          this.$message.error(error.msg);
        }
      } finally {
        this.submitting = false;
      }
    }
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }
}
</script>

<style lang="scss" scoped>
.forgot-password-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 199;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  
  .forgot-password-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
  }
  
  .forgot-password-content {
    position: relative;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    max-width: 480px;
    width: 100%;
    max-height: 85vh;
    overflow: hidden;
    animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform-origin: center;
    
    .forgot-password-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 24px 28px;
      border-bottom: 1px solid #e9ecef;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      
      h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        color: #2c3e50;
      }
      
      .close-btn {
        background: none;
        border: none;
        font-size: 22px;
        color: #6c757d;
        cursor: pointer;
        padding: 8px;
        border-radius: 8px;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        
        &:hover {
          background: #e9ecef;
          color: #495057;
          transform: scale(1.1);
        }
      }
    }
    
    .forgot-password-body {
      padding: 24px 28px 28px;
    }
  }
}

// 方法选择器样式
.method-selector {
  display: flex;
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  
  .method-option {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 20px;
    background: #f8f9fa;
    cursor: pointer;
    transition: all 0.3s ease;
    border-right: 1px solid #e9ecef;
    
    &:last-child {
      border-right: none;
    }
    
    i {
      margin-right: 8px;
      font-size: 18px;
      color: #666;
    }
    
    span {
      font-size: 16px;
      font-weight: 500;
      color: #666;
    }
    
    &.active {
      background: #09CA6A;
      color: white;
      
      i, span {
        color: white;
      }
    }
    
    &:hover:not(.active) {
      background: #e9ecef;
    }
  }
}

.input-box {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  .area-box {
    height: 56px;
    width: 114px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
  }

  .shadow-box {
    flex: 1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    position: relative;
    
    &.full-width {
      flex: 1;
    }
    
    input {
      height: 56px;
      width: 100%;
      border: none;
      outline: none;
      padding-left: 20px;
      padding-right: 50px;
      font-size: 16px;
      font-weight: 600;
      background: transparent;
      &::placeholder {
        font-weight: 500;
      }
    }
    
    .password-toggle {
      position: absolute;
      right: 15px;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
      font-size: 18px;
      color: #999;
      transition: all 0.3s ease;
      
      &:hover {
        color: #666;
        transform: translateY(-50%) scale(1.1);
      }
      
      &:active {
        transform: translateY(-50%) scale(0.95);
      }
      
      &.show {
        color: #09ca6a;
        
        &:hover {
          color: #08b35e;
        }
      }
    }
  }
}

.send-code-btn {
  height: 56px;
  padding: 0 20px;
  background: #09CA6A;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  white-space: nowrap;
  
  &:hover:not(.disabled) {
    background: #0fda74;
  }
  
  &.disabled {
    background: #999;
    cursor: not-allowed;
  }
  
  i {
    margin-right: 5px;
  }
}

.submit-btn {
  width: 100%;
  height: 58px;
  background: #09CA6A;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 29px;
  box-shadow: 0 4px 12px rgba(9, 202, 106, 0.3);
  transition: all .3s ease;
  cursor: pointer;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  
  &:hover:not(.disabled) {
    background: #0fda74;
    box-shadow: 0 6px 20px rgba(9, 202, 106, 0.4);
    transform: translateY(-2px);
  }
  
  &.disabled {
    background: #999;
    cursor: not-allowed;
    &:hover {
      background: #999;
      transform: none;
      box-shadow: 0 4px 12px rgba(9, 202, 106, 0.3);
    }
  }
}

// Cloudflare 弹层样式
.cloudflare-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  
  .cloudflare-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
  }
  
  .cloudflare-content {
    position: relative;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    max-width: 480px;
    width: 100%;
    max-height: 85vh;
    overflow: hidden;
    animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform-origin: center;
    
    .cloudflare-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 24px 28px;
      border-bottom: 1px solid #e9ecef;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      
      h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        color: #2c3e50;
      }
      
      .close-btn {
        background: none;
        border: none;
        font-size: 22px;
        color: #6c757d;
        cursor: pointer;
        padding: 8px;
        border-radius: 8px;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        
        &:hover {
          background: #e9ecef;
          color: #495057;
          transform: scale(1.1);
        }
      }
    }
  }
}

@keyframes modalSlideIn {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-30px);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05) translateY(-5px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media screen and (max-width: 950px) {
  .forgot-password-modal {
    padding: 10px;
    
    .forgot-password-content {
      max-width: 100%;
      
      .forgot-password-body {
        padding: 20px;
      }
    }
  }
  
  .method-selector {
    margin-bottom: 15px;
    
    .method-option {
      padding: 12px 15px;
      
      i {
        font-size: 16px;
        margin-right: 6px;
      }
      
      span {
        font-size: 14px;
      }
    }
  }
  
  .input-box {
    margin-bottom: 15px;
    
    .area-box {
      height: 50px;
      width: 100px;
    }
    
    .shadow-box {
      input {
        height: 50px;
        font-size: 14px;
      }
    }
  }
  
  .send-code-btn {
    height: 50px;
    padding: 0 15px;
    font-size: 12px;
    min-width: 100px;
  }
  
  .submit-btn {
    height: 50px;
    font-size: 14px;
  }
}
</style>
